import React, { useState, useEffect } from 'react';
import { taskService } from '../services';
import { DataTable } from '../components';
import { useInterval, useSideDialog, useModalDialog, useGlobalState, useMessage } from '../hooks';
import { formatData, handleError, Role, formatDuration, cronDescription } from '../helpers';

export const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();

  // Update job steps continuously
  useInterval(() => {
    fetchItems();
  }, 3000);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    taskService
      .getAll()
      .then(res => {
        if (res?.tasks) {
          let maxValue = 0;

          res.tasks.forEach(row => {
            if (row.job_runtime_seconds > maxValue) {
              maxValue = row.job_runtime_seconds;
            }
          });

          const tasks_tmp = res.tasks.map(task => {
            const nextRunCronDesc = cronDescription(task.next_schedule_cron);
            return {
              ...task,
              task_is_enabled_tooltip:
                !task.task_is_enabled && task.consecutive_failure_count > 0
                  ? `Auto-disabled after ${task.consecutive_failure_count} consecutive scheduled failure${task.consecutive_failure_count === 1 ? '' : 's'}`
                  : undefined,
              job_runtime_seconds_max_value: maxValue,
              job_runtime_tooltip: formatDuration(task.job_runtime_seconds) + ' (hh:mm:ss)',
              connector_name_tooltip:
                task.connector_code === 'odbc'
                  ? `${task.connector_name} (${task.connector_driver_name})`
                  : task.connector_name,
              next_run_display: task.next_run_timestamp
                ? !task.task_is_enabled
                  ? 'Disabled'
                  : formatData(task.next_run_timestamp, 'datetime')
                : 'No schedule',
              next_run_className:
                task.next_run_timestamp && task.task_is_enabled ? '' : 'small-text',
              connection_name_display: task.connection_name ?? 'N/A',
              connection_name_className: task.connection_name ? '' : 'small-text',
              connection_link: task.connection_id
                ? `/connections/${task.connection_id}`
                : undefined,
              next_run_tooltip:
                task.next_schedule_name && task.task_is_enabled
                  ? `${task.next_schedule_name} — ${nextRunCronDesc}`
                  : undefined,
              // Running tasks get position 0 so they sort above queued tasks when sorting by this column.
              job_queue_position: task.job_status === 'running' ? ' ' : task.job_queue_position,
              task_is_active: task.job_status === 'running' || task.job_queue_position != null,
            };
          });
          setTasks(tasks_tmp);
          setLoading(false);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const updateJobStatus = (task_arr, setNewState, jobs) => {
    let tasks_tmp = task_arr;
    let i = -1;
    let tmp = null;

    jobs.forEach(job => {
      i = task_arr.findIndex(x => x.task_id === job.task_id);
      tmp = tasks_tmp[i];
      tasks_tmp[i] = {
        ...tmp,
        job_status: job.job_status,
        job_completion_message: job.job_completion_message,
      };
    });
    setNewState([...tasks_tmp]);
  };

  const addTask = async data => {
    const tasks = { tasks: Array.isArray(data) ? data : [data] };

    await taskService
      .create(tasks)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editTask = async data => {
    const tasks = { tasks: Array.isArray(data) ? data : [data] };

    await taskService
      .update(tasks)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  /**
   * Enqueues one or more tasks for execution via a single batch request.
   * Optimistically marks selected tasks as 'queued' in the UI before the request completes.
   * Tasks that are already active or disabled are skipped and described in the response message.
   * @param {Object[]} data - Selected task objects.
   */
  const runTask = async data => {
    const selectedTasks = data || selectedItems;

    // Optimistically update UI for all selected tasks before the network round-trip.
    updateJobStatus(
      tasks,
      setTasks,
      selectedTasks.map(task => ({
        task_id: task.task_id,
        job_status: 'queued',
        job_completion_message: 'Queued at ' + new Date().toLocaleString('sv-SE'),
      }))
    );

    await taskService
      .runMany(selectedTasks.map(t => t.task_id))
      .then(res => {
        res && showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => {
        fetchItems();
      });
  };

  /**
   * Aborts one or more tasks via a single batch request.
   * Queued tasks are dequeued immediately; running tasks receive an abort signal and will
   * transition to 'aborted' asynchronously.
   * @param {Object[]} data - Selected task objects.
   */
  const abortTask = async data => {
    const selectedTasks = data || selectedItems;

    await taskService
      .abortMany(selectedTasks.map(t => t.task_id))
      .then(res => res && showMessage(res.message))
      .catch(err => showMessage(err, 'error'))
      .finally(() => {
        fetchItems();
      });
  };

  const deleteTask = async data => {
    await taskService
      .destroy({ tasks: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;
  const hasQueuedTasks = tasks.some(t => t.job_queue_position != null);

  const taskIsEnabledValues = {
    true: { label: 'Enabled', className: 'pill' },
    false: { label: 'Disabled', className: 'pill pill-semifull-red' },
  };

  return (
    <div className="Tasks wide-margin">
      <DataTable
        columns={[
          ...(hasQueuedTasks
            ? [
                {
                  id: 'job_queue_position',
                  name: '',
                  type: 'integer',
                  align: 'right',
                  className: 'small-text',
                },
              ]
            : []),
          { id: 'job_status', name: '', type: 'icon', tooltip: 'job_completion_message' },
          {
            id: 'task_name',
            name: 'Name',
            link: '/tasks/:task_id',
            tooltip: 'task_description',
            className: 'bold',
          },
          {
            id: 'task_type_name',
            name: 'Type',
            className: 'small-text',
            tooltip: 'task_type_description',
          },
          {
            id: 'connector_code',
            name: '',
            type: 'icon',
            tooltip: 'connector_name_tooltip',
            className: 'no-right-margin',
          },
          {
            id: 'connection_name_display',
            name: 'Connection',
            link: ':connection_link',
            tooltip: 'connection_description',
            className: ':connection_name_className',
          },
          { id: 'job_completed_timestamp', name: 'Last Run Completed', type: 'datetime' },
          {
            id: 'job_runtime_seconds',
            name: 'Runtime [s]',
            type: 'chart',
            chartType: 'bar',
            data: { data: 'job_runtime_seconds', max: 'job_runtime_seconds_max_value' },
            tooltip: 'job_runtime_tooltip',
          },
          {
            id: 'next_run_display',
            name: 'Next Run',
            tooltip: 'next_run_tooltip',
            className: ':next_run_className',
          },
          { id: 'task_priority', name: 'Priority', type: 'integer', align: 'right' },
          {
            id: 'task_is_enabled',
            name: 'Status',
            type: 'boolean',
            values: taskIsEnabledValues,
            tooltip: 'task_is_enabled_tooltip',
          },
        ]}
        buttons={[
          {
            label: '+ Add',
            action: 'add',
            mainButton: true,
            onClick: items => showSideDialog('addTask', items || selectedItems, addTask),
          },
          {
            label: 'Run',
            action: 'run',
            onClick: runTask,
            disabled: [
              {
                field: 'job_status',
                value: 'running',
                tooltip: 'One or more selected tasks are already running',
              },
              {
                field: 'task_is_enabled',
                value: false,
                tooltip: 'All selected tasks are disabled',
              },
            ],
          },
          {
            label: 'Cancel',
            action: 'abort',
            onClick: abortTask,
            hidden: {
              field: 'task_is_active',
              value: false,
            },
          },
          {
            label: 'Edit',
            action: 'edit',
            onClick: items => showSideDialog('editTask', items || selectedItems, editTask),
          },
          {
            label: 'Delete',
            action: 'delete',
            onClick: items => showModalDialog('deleteTask', items || selectedItems, deleteTask),
          },
        ]}
        data={tasks}
        title="Tasks"
        idColumn="task_id"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        filterable={true}
        filterObjects={[
          'task_name',
          'task_type_name',
          'task_description',
          'connection_name',
          'task_is_enabled',
        ]}
        filterObjectsValueMap={{ task_is_enabled: taskIsEnabledValues }}
      />
    </div>
  );
};
